import React, { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { formatPrice } from "@/utils/checkout";
import { Receipt, Download, XCircle, RefreshCcw, CheckCircle2, Loader2, CreditCard } from "lucide-react";

const KIND_LABEL = {
  one_time: "One-time",
  subscription_3x: "3-month plan",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}
function fmtEpoch(sec) {
  if (!sec) return null;
  try { return new Date(sec * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return null; }
}

export default function BillingPanel() {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [subs, setSubs] = useState([]);
  const [busyReceipt, setBusyReceipt] = useState(null);
  const [busyCancel, setBusyCancel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.all([
        api.get("/api/payments/history").then((r) => r.data).catch(() => []),
        api.get("/api/subscriptions/mine").then((r) => r.data).catch(() => []),
      ]);
      setHistory(Array.isArray(h) ? h : []);
      setSubs(Array.isArray(s) ? s : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openReceipt = async (sessionId) => {
    setBusyReceipt(sessionId);
    try {
      const { data } = await api.get(`/api/payments/receipt/${sessionId}`);
      if (data?.receipt_url) window.open(data.receipt_url, "_blank", "noopener,noreferrer");
      else toast.error("Receipt not available yet");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Receipt unavailable";
      toast.error(msg);
    } finally {
      setBusyReceipt(null);
    }
  };

  const cancelSub = async (subId) => {
    if (!window.confirm("Cancel this subscription? You'll keep access to what's already paid for.")) return;
    setBusyCancel(subId);
    try {
      await api.delete(`/api/subscriptions/${subId}`);
      toast.success("Subscription cancelled");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Cancel failed";
      toast.error(msg);
    } finally {
      setBusyCancel(null);
    }
  };

  return (
    <div className="vbs-card" style={{ padding: 26, marginTop: 22 }} data-testid="billing-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <CreditCard size={18} style={{ color: "var(--gold)" }} />
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, color: "var(--bright)" }}>
          Billing <em style={{ color: "var(--gold)" }}>&amp; Receipts</em>
        </div>
        <button
          onClick={load}
          className="vbs-btn vbs-btn-ghost"
          style={{ marginLeft: "auto", padding: "6px 12px", fontSize: 12 }}
          disabled={loading}
          data-testid="billing-refresh"
        >
          <RefreshCcw size={12} /> Refresh
        </button>
      </div>
      <p style={{ fontSize: 13, color: "var(--mid)", marginBottom: 18 }}>
        Every payment you make is here. Download a Stripe-hosted receipt for any transaction, and manage any active 3-month plans.
      </p>

      {/* Active subscriptions */}
      {subs.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase", marginBottom: 10 }}>
            Active Plans
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {subs.map((s) => (
              <div
                key={s.id}
                className="vbs-row"
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px", border: "1px solid var(--border)", borderRadius: 4,
                  background: "rgba(201,168,76,0.04)",
                }}
                data-testid={`sub-row-${s.id}`}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ color: "var(--bright)", fontSize: 14, fontWeight: 500 }}>
                    {s.tier_id ? (s.tier_id.replace("_", " ")) : "Subscription"}
                    <span style={{ marginLeft: 10, fontSize: 11, color: "var(--gold)", letterSpacing: 1.5, textTransform: "uppercase" }}>
                      {s.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--gray)", marginTop: 3 }}>
                    {s.amount != null && s.currency ? `${formatPrice(s.amount)} ${s.currency}/${s.interval || "mo"}` : ""}
                    {s.billing_cycles ? ` · capped at ${s.billing_cycles} payments` : ""}
                    {s.current_period_end ? ` · next on ${fmtEpoch(s.current_period_end)}` : ""}
                    {s.cancel_at_period_end ? " · cancels at period end" : ""}
                  </div>
                </div>
                {["active", "trialing", "past_due"].includes(s.status) && (
                  <button
                    onClick={() => cancelSub(s.id)}
                    className="vbs-btn vbs-btn-coral"
                    disabled={busyCancel === s.id}
                    data-testid={`sub-cancel-${s.id}`}
                    style={{ padding: "6px 12px", fontSize: 12 }}
                  >
                    {busyCancel === s.id ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment history */}
      <div style={{ fontSize: 11, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase", marginBottom: 10 }}>
        Payment History
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: "center", color: "var(--gray)" }} data-testid="billing-loading">
          <Loader2 className="animate-spin" size={18} style={{ marginRight: 8, verticalAlign: "-3px" }} />
          Loading your payments…
        </div>
      ) : history.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--gray)", fontSize: 13 }} data-testid="billing-empty">
          No payments yet. Any purchases you make will show up here with a downloadable receipt.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {history.map((tx) => {
            const paid = tx.payment_status === "paid" || tx.status === "complete";
            const sid = tx.session_id;
            return (
              <div
                key={sid}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "13px 16px", border: "1px solid var(--border)", borderRadius: 4,
                }}
                data-testid={`tx-row-${sid}`}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "var(--bright)", fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                    {tx.tier_id?.replace("_", " ") || "Purchase"}
                    <span style={{
                      fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
                      color: paid ? "#6dd4a0" : "var(--gray)",
                      display: "inline-flex", alignItems: "center", gap: 3,
                    }}>
                      {paid ? <><CheckCircle2 size={10} /> Paid</> : (tx.status || "pending")}
                    </span>
                    {tx.stripe_mode === "test" && (
                      <span style={{ fontSize: 10, color: "#c9a84c", letterSpacing: 1.5 }}>TEST</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--gray)", marginTop: 3, fontFamily: "var(--font-mono, monospace)" }}>
                    {fmtDate(tx.created_at)} · {KIND_LABEL[tx.kind] || tx.kind} · {formatPrice(tx.amount)} {(tx.currency || "usd").toUpperCase()}
                  </div>
                </div>
                {paid && (
                  <button
                    onClick={() => openReceipt(sid)}
                    disabled={busyReceipt === sid}
                    className="vbs-btn vbs-btn-ghost"
                    style={{ padding: "6px 12px", fontSize: 12 }}
                    data-testid={`tx-receipt-${sid}`}
                    title="Open Stripe receipt"
                  >
                    {busyReceipt === sid ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Receipt
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--gray)" }}>
        <Receipt size={12} /> Receipts are hosted securely by Stripe and always reflect the card and address used at checkout.
      </div>
    </div>
  );
}
